import { useState } from "react";
import { useTranslation } from "react-i18next";
import { inputCls, labelCls } from "components/tw/classes";
import { EditPanel, useSaveWithToast } from "containers/settings/ui";
import { saveCompanyHoliday } from "./companyHolidays.service";

/** `id` is set when an existing holiday is being changed, empty for a new one. */
export type HolidayDraft = {
  id?: string;
  title: string;
  startDate: string;
  endDate: string;
};

type HolidayFormProps = {
  companyId: string;
  /** The year whose list is on screen: a holiday starts in the year it is listed under. */
  year: number;
  initial: HolidayDraft;
  onSaved: () => void;
  onCancel: () => void;
};

/**
 * Add or change one holiday, inline above the list. Mounted only while open,
 * so closing it throws away the draft and the last error.
 */
export function HolidayForm({
  companyId,
  year,
  initial,
  onSaved,
  onCancel,
}: HolidayFormProps) {
  const { t } = useTranslation();
  const { save, saving, error: saveError } = useSaveWithToast();
  const [draft, setDraft] = useState(initial);
  const [mistake, setMistake] = useState<string | null>(null);

  // A one-day holiday only needs the first date, so the last one follows it
  // until it is set to something later on purpose.
  const changeStartDate = (startDate: string) =>
    setDraft({
      ...draft,
      startDate,
      endDate: draft.endDate >= startDate ? draft.endDate : startDate,
    });

  const findMistake = (): string | null => {
    if (!draft.title.trim() || !draft.startDate || !draft.endDate) {
      return t("settings_holidays_form_incomplete");
    }
    if (!draft.startDate.startsWith(`${year}-`)) {
      return t("settings_holidays_start_wrong_year", { year });
    }
    if (draft.endDate < draft.startDate) {
      return t("settings_holidays_end_before_start");
    }
    return null;
  };

  // The backend answers a change to a holiday that has already begun with this code.
  const saveErrorMessage = (err: unknown) =>
    (err as { response?: { data?: unknown } }).response?.data ===
    "CANNOT_EDIT_PAST_HOLIDAY"
      ? t("settings_holidays_past_forbidden")
      : t("settings_save_failed");

  const submit = async () => {
    const found = findMistake();
    setMistake(found);
    if (found) return;

    const saved = await save(
      () =>
        saveCompanyHoliday(companyId, {
          id: draft.id,
          title: draft.title,
          startDate: draft.startDate,
          endDate: draft.endDate,
        }),
      { errorMessage: saveErrorMessage }
    );
    if (saved) onSaved();
  };

  return (
    <EditPanel
      onSave={submit}
      onCancel={onCancel}
      saving={saving}
      error={mistake ?? saveError}
    >
      <p className="mb-3 text-sm font-medium text-gray-900">
        {draft.id
          ? t("settings_holidays_form_edit_title")
          : t("settings_holidays_form_add_title")}
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label htmlFor="holiday-title" className={labelCls}>
            {t("settings_holidays_field_name")}
          </label>
          <input
            id="holiday-title"
            type="text"
            className={inputCls}
            value={draft.title}
            onChange={(event) => setDraft({ ...draft, title: event.target.value })}
          />
        </div>
        <div>
          <label htmlFor="holiday-start" className={labelCls}>
            {t("settings_holidays_field_start")}
          </label>
          <input
            id="holiday-start"
            type="date"
            className={inputCls}
            min={`${year}-01-01`}
            max={`${year}-12-31`}
            value={draft.startDate}
            onChange={(event) => changeStartDate(event.target.value)}
          />
        </div>
        <div>
          <label htmlFor="holiday-end" className={labelCls}>
            {t("settings_holidays_field_end")}
          </label>
          <input
            id="holiday-end"
            type="date"
            className={inputCls}
            min={draft.startDate || undefined}
            value={draft.endDate}
            onChange={(event) => setDraft({ ...draft, endDate: event.target.value })}
          />
        </div>
      </div>
    </EditPanel>
  );
}
